import { useMemo, useState } from 'react';
import { Type } from 'lucide-react';
import CaixasTextoPlanoOverlay from './CaixasTextoPlanoOverlay';
import type { GrupoCorte, ConfiguracaoChapa, PecaAcabada } from '../../types/corte';
import type { CaixaTextoPlano } from '../../types/caixaTextoPlano';
import { criarCaixaTextoPlano } from '../../types/caixaTextoPlano';

interface VisualizacaoCorteProps {
  grupos: GrupoCorte[];
  configuracao: ConfiguracaoChapa;
  projeto: { nomePeca: string; nomeProduto: string };
  rotacao: number;
  pecasAcabadas: PecaAcabada[];
  isPreview?: boolean;
  /** Só o desenho SVG — usado no hover do catálogo. */
  modoCompacto?: boolean;
  mostrarResumo?: boolean;
  layoutConfig?: {
    desenhoX: number;
    desenhoY: number;
    desenhoLargura: number;
    desenhoAltura: number;
    desenhoRotacao: number;
    desenhoEscalaX: number;
    desenhoEscalaY: number;
    infoX: number;
    infoY: number;
    infoLargura: number;
    infoAltura: number;
    infoRotacao: number;
    fontSize: number;
    fontSizeInfo: number;
    observacoes: string;
    obsX: number;
    obsY: number;
    obsLargura: number;
    obsAltura: number;
    obsRotacao: number;
    obsFontSize: number;
  };
  onLayoutChange?: (config: VisualizacaoCorteProps['layoutConfig']) => void;
  rotacaoFolha?: number;
  /** Caixas de texto sobre o desenho (opcional — só ativo quando onCaixasTextoChange é passado). */
  caixasTexto?: CaixaTextoPlano[];
  onCaixasTextoChange?: (caixas: CaixaTextoPlano[]) => void;
}

interface PecaVisual {
  x: number;
  y: number;
  width: number;
  height: number;
  medidaTransversal: number;
  medidaLongitudinal: number;
  codigo: number;
  grupoIndex: number;
}

type BlocoSequencialVisual = {
  longMed: number;
  longQtd: number;
  transMed: number;
  transQtd: number;
  codigoTrans: number;
  subLongCortes?: Array<{ med: number; qtd: number; inicio: number }>;
};

type TiraSequencialVisual = {
  longMed: number;
  longQtd: number;
  transversais: Array<{ med: number; qtd: number; codigo: number; subLongCortes?: Array<{ med: number; qtd: number; inicio: number }> }>;
};

export default function VisualizacaoCorte({ grupos, configuracao, projeto, rotacao, pecasAcabadas, isPreview = false, modoCompacto = false, mostrarResumo = true, layoutConfig, rotacaoFolha = 0, caixasTexto = [], onCaixasTextoChange }: VisualizacaoCorteProps) {
  const larguraTotal = Number(configuracao?.larguraTotal) || 2200;
  const comprimentoTotal = Number(configuracao?.comprimentoTotal) || 2750;
  const espessuraSerra = Number(configuracao?.espessuraSerra) || 4;

  const [lupaAtiva, setLupaAtiva] = useState(false);
  const [lupaZoom, setLupaZoom] = useState(3);
  const [lupaPos, setLupaPos] = useState<{ x: number; y: number } | null>(null);
  const [caixaSelecionadaId, setCaixaSelecionadaId] = useState<string | null>(null);
  const lupaTamanhoPx = 220;
  const editarCaixasTexto = Boolean(onCaixasTextoChange);
  
  const defLayout = {
    desenhoX: 5, desenhoY: 35, desenhoLargura: 130, desenhoAltura: 240, desenhoRotacao: 0, desenhoEscalaX: 100, desenhoEscalaY: 100,
    infoX: 140, infoY: 35, infoLargura: 65, infoAltura: 200, infoRotacao: 0, fontSize: 100, fontSizeInfo: 100,
    observacoes: '', obsX: 5, obsY: 250, obsLargura: 200, obsAltura: 35, obsRotacao: 0, obsFontSize: 100
  };
  const layout = layoutConfig && typeof layoutConfig === 'object' ? { ...defLayout, ...layoutConfig } : defLayout;
  
  const maxWidth = 600;
  const maxHeight = 450;
  const chapaLargura = Math.max(larguraTotal, 1);
  const chapaComprimento = Math.max(comprimentoTotal, 1);
  const scale = Math.min(maxWidth / chapaLargura, maxHeight / chapaComprimento);
  const svgWidth = chapaLargura * scale;
  const svgHeight = chapaComprimento * scale;
  
  const pecasVisuais: PecaVisual[] = [];
  let offsetXGlobal = 0;

  const extrairBlocosSequenciais = (pecas: any[]): BlocoSequencialVisual[] => {
    const out: BlocoSequencialVisual[] = [];
    let longMed = 0;
    let longQtd = 1;
    let transRefMed = 0;
    let transRefQtd = 1;
    let restanteLong5 = 0;
    (pecas || []).forEach((p) => {
      if (p.codigo === 3) {
        longMed = Number(p.medida) || 0;
        longQtd = Math.max(1, Number(p.quantidade) || 1);
        return;
      }
      if (p.codigo === 4 || p.codigo === 44) {
        if (longMed <= 0) return;
        const blocoBase: BlocoSequencialVisual = {
          longMed,
          longQtd,
          transMed: Number(p.medida) || 0,
          transQtd: Math.max(1, Number(p.quantidade) || 1),
          codigoTrans: p.codigo ?? 4,
        };
        out.push(blocoBase);
        transRefMed = blocoBase.transMed;
        transRefQtd = blocoBase.transQtd;
        restanteLong5 = longMed;
        return;
      }
      if (p.codigo === 5) {
        if (out.length === 0 || transRefMed <= 0) return;
        const last = out[out.length - 1];
        if (!last) return;
        const baseLong = restanteLong5 > 0 ? restanteLong5 : longMed;
        if (baseLong <= 0) return;
        const qtd5 = Math.max(1, Number(p.quantidade) || 1);
        const corteInformado = Number(p.medida) || baseLong;
        const corteEfetivo = Math.min(corteInformado, baseLong);
        const inicio = Math.max(0, longMed - baseLong);
        if (!last.subLongCortes) last.subLongCortes = [];
        last.subLongCortes.push({ med: corteEfetivo, qtd: qtd5, inicio });
        restanteLong5 = Math.max(0, baseLong - (corteEfetivo + espessuraSerra) * qtd5);
      }
    });
    return out;
  };

  const agruparEmTiras = (blocos: BlocoSequencialVisual[]): TiraSequencialVisual[] => {
    const tiras: TiraSequencialVisual[] = [];
    blocos.forEach((b) => {
      const last = tiras[tiras.length - 1];
      const podeMesclar =
        !!last &&
        last.longMed === b.longMed &&
        last.longQtd === b.longQtd;
      const transEntry: { med: number; qtd: number; codigo: number; subLongCortes?: Array<{ med: number; qtd: number; inicio: number }> } = {
        med: b.transMed,
        qtd: b.transQtd,
        codigo: b.subLongCortes && b.subLongCortes.length > 0 ? 5 : (b.codigoTrans ?? 4),
        ...(b.subLongCortes && b.subLongCortes.length > 0 ? { subLongCortes: b.subLongCortes } : {}),
      };
      if (podeMesclar) {
        last.transversais.push(transEntry);
      } else {
        tiras.push({
          longMed: b.longMed,
          longQtd: b.longQtd,
          transversais: [transEntry],
        });
      }
    });
    return tiras;
  };
  
  grupos.forEach((grupo, grupoIndex) => {
    const blocos = extrairBlocosSequenciais(grupo.pecas);
    const tiras = agruparEmTiras(blocos);
    if (tiras.length === 0) return;
    let offsetXGrupo = offsetXGlobal;

    for (let ti = 0; ti < tiras.length; ti++) {
      const t = tiras[ti];
      const medLong = t.longMed;
      const qtdLong = t.longQtd;
      const hasNextLongType = ti < tiras.length - 1;

      for (let l = 0; l < qtdLong; l++) {
        let offsetY = 0;
        t.transversais.forEach((tr) => {
          if (tr.subLongCortes && tr.subLongCortes.length > 0) {
            for (let q = 0; q < tr.qtd; q++) {
              tr.subLongCortes.forEach((corte) => {
                for (let sub = 0; sub < corte.qtd; sub++) {
                  pecasVisuais.push({
                    x: offsetXGrupo + corte.inicio + sub * (corte.med + espessuraSerra),
                    y: offsetY,
                    width: corte.med,
                    height: tr.med,
                    medidaTransversal: tr.med,
                    medidaLongitudinal: corte.med,
                    codigo: 5,
                    grupoIndex: grupoIndex
                  });
                }
              });
              offsetY += tr.med + (q < tr.qtd - 1 ? espessuraSerra : 0);
            }
          } else {
            for (let q = 0; q < tr.qtd; q++) {
              pecasVisuais.push({
                x: offsetXGrupo,
                y: offsetY,
                width: medLong,
                height: tr.med,
                medidaTransversal: tr.med,
                medidaLongitudinal: medLong,
                codigo: tr.codigo ?? 4,
                grupoIndex: grupoIndex
              });
              offsetY += tr.med + (q < tr.qtd - 1 ? espessuraSerra : 0);
            }
          }
        });
        const isLast = l === qtdLong - 1;
        offsetXGrupo += medLong + ((!isLast || hasNextLongType) ? espessuraSerra : 0);
      }
    }
    offsetXGlobal = offsetXGrupo;
  });
  
  const totalConsumoLargura = offsetXGlobal;
  const maxConsumoComprimento = Math.max(...pecasVisuais.map(p => p.y + p.height), 0);
  
  const getCorPeca = (codigo: number) => {
    if (codigo === 3) return { fill: '#bbf7d0', stroke: '#22c55e', text: '#166534' };
    if (codigo === 5) return { fill: '#e9d5ff', stroke: '#9333ea', text: '#6b21a8' };
    return { fill: '#bfdbfe', stroke: '#3b82f6', text: '#1e40af' };
  };

  const svgElement = useMemo(() => {
    return (
      <svg
        width={svgWidth + 50}
        height={svgHeight + 40}
        className="border-2 border-[#009b3a]/22 rounded-lg bg-[#eef0f4] shadow-inner"
      >
        <rect x={25} y={20} width={svgWidth} height={svgHeight} fill="#fef9c3" stroke="#64748b" strokeWidth="2" />
        {pecasVisuais.map((peca, index) => {
          const cor = getCorPeca(peca.codigo);
          const drawX = 25 + peca.x * scale;
          const drawY = 20 + peca.y * scale;
          const drawWidth = Math.max(peca.width * scale, 2);
          const drawHeight = Math.max(peca.height * scale, 2);
          // Se a peça for "vertical", gira o texto para acompanhar o lado maior (igual ao Projeto)
          const isVertical = drawHeight > drawWidth;
          const fontSize = Math.max(8, Math.min((isVertical ? drawHeight : drawWidth) / 6, 14));
          // Formato: Transversal × Longitudinal
          const medidaTexto = `${peca.medidaTransversal}×${peca.medidaLongitudinal}`;
          return (
            <g key={`peca-${index}`}>
              <rect x={drawX} y={drawY} width={drawWidth} height={drawHeight} fill={cor.fill} stroke={cor.stroke} strokeWidth="1" />
              <text
                x={drawX + drawWidth / 2}
                y={drawY + drawHeight / 2}
                textAnchor="middle"
                dominantBaseline="middle"
                fontSize={fontSize}
                fontWeight="bold"
                fill={cor.text}
                transform={isVertical ? `rotate(-90 ${drawX + drawWidth / 2} ${drawY + drawHeight / 2})` : undefined}
              >
                {medidaTexto}
              </text>
            </g>
          );
        })}
      </svg>
    );
  }, [getCorPeca, pecasVisuais, scale, svgHeight, svgWidth]);

  const renderCaixasSobreChapa = (
    chapaW: number,
    chapaH: number,
    caixas: CaixaTextoPlano[],
    offsetX = 25,
    offsetY = 20,
    rotacaoCompensada = rotacao
  ) => {
    if (!caixas.length) return null;
    const rotNorm = ((rotacaoCompensada % 360) + 360) % 360;
    const contra = rotNorm ? `rotate(-${rotNorm}deg)` : undefined;
    return (
      <div
        className="pointer-events-none absolute z-10"
        style={{ left: offsetX, top: offsetY, width: chapaW, height: chapaH }}
      >
        {caixas.map((caixa) => (
          <div
            key={caixa.id}
            className="absolute overflow-visible"
            style={{
              left: `${caixa.xPct}%`,
              top: `${caixa.yPct}%`,
              width: `${caixa.wPct}%`,
              height: `${caixa.hPct}%`,
            }}
          >
            <div
              className="h-full w-full overflow-auto rounded border border-slate-500/70 bg-white/90 px-1 py-0.5"
              style={{
                fontSize: `${caixa.fontSizePt}pt`,
                lineHeight: 1.25,
                writingMode: 'horizontal-tb',
                transform: contra,
                transformOrigin: 'center center',
              }}
            >
              <span className="whitespace-pre-wrap break-words text-black">{caixa.texto}</span>
            </div>
          </div>
        ))}
      </div>
    );
  };
  
  if (pecasVisuais.length === 0) {
    return <div className="bg-[#e4e6ec] rounded-xl p-8 text-center text-neutral-600"><p className="text-lg">Adicione peças com código 3 e 4/44 para visualizar o plano de corte</p></div>;
  }
  
  const todasPecas = grupos.flatMap(g => g.pecas);
  
  const renderDesenhoSVG = (width: number, height: number) => {
    const scaleX = (width / chapaLargura) * (layout.desenhoEscalaX / 100);
    const scaleY = (height / chapaComprimento) * (layout.desenhoEscalaY / 100);
    const currentSvgWidth = chapaLargura * scaleX;
    const currentSvgHeight = chapaComprimento * scaleY;
    const fontMult = layout.fontSize / 100;
    
    return (
      <svg width={currentSvgWidth} height={currentSvgHeight} viewBox={`0 0 ${currentSvgWidth} ${currentSvgHeight}`} style={{ width: currentSvgWidth, height: currentSvgHeight }}>
        <rect x={0} y={0} width={currentSvgWidth} height={currentSvgHeight} fill="#fef9c3" stroke="#94a3b8" strokeWidth={0.5} />
        {pecasVisuais.map((peca, index) => {
          const cor = getCorPeca(peca.codigo);
          const drawX = peca.x * scaleX;
          const drawY = peca.y * scaleY;
          const drawWidth = Math.max(peca.width * scaleX, 0.5);
          const drawHeight = Math.max(peca.height * scaleY, 0.5);
          const medidaTexto = `${peca.medidaTransversal}×${peca.medidaLongitudinal}`;
          
          // Se altura > largura, girar texto para acompanhar a medida maior
          const isVertical = drawHeight > drawWidth;
          const fontSize = Math.max(2, Math.min(isVertical ? drawHeight / 6 : drawWidth / 6, 6) * fontMult);
          
          return (
            <g key={`p-${index}`}>
              <rect x={drawX} y={drawY} width={drawWidth} height={drawHeight} fill={cor.fill} stroke={cor.stroke} strokeWidth={0.3} />
              {drawWidth > 5 && drawHeight > 4 && (
                <text 
                  x={drawX + drawWidth / 2} 
                  y={drawY + drawHeight / 2} 
                  textAnchor="middle" 
                  dominantBaseline="middle" 
                  fontSize={fontSize} 
                  fontWeight="700" 
                  fill={cor.text}
                  transform={isVertical ? `rotate(-90 ${drawX + drawWidth / 2} ${drawY + drawHeight / 2})` : undefined}
                >{medidaTexto}</text>
              )}
            </g>
          );
        })}
      </svg>
    );
  };
  
  // Pré-visualização A4
  if (isPreview) {
    const isPaisagem = rotacaoFolha === 90 || rotacaoFolha === 270;
    const folhaLargura = isPaisagem ? '297mm' : '210mm';
    const folhaAltura = isPaisagem ? '210mm' : '297mm';
    
    return (
      <div className="bg-[#dde0e6] p-4 rounded-xl flex justify-center overflow-auto">
        <div className="bg-[#fafbfc] shadow-2xl relative" style={{ width: folhaLargura, minHeight: folhaAltura, transform: 'scale(0.5)', transformOrigin: 'top center' }}>
          <div className="p-2 flex flex-col" style={{ width: folhaLargura, height: folhaAltura }}>
            <div className="flex justify-between items-start px-3 py-2 mb-2 border-b-2 border-[#009b3a]/25">
              <div>
                <h1 className="text-lg font-bold text-black">Planos de Corte Ade</h1>
                <p className="text-xs text-neutral-700"><strong>Peça:</strong> {projeto.nomePeca || '-'}</p>
                <p className="text-xs text-neutral-700"><strong>Produto:</strong> {projeto.nomeProduto || '-'}</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-neutral-700"><strong>Chapa:</strong> {larguraTotal} × {comprimentoTotal} mm</p>
                <p className="text-xs text-neutral-700"><strong>Serra:</strong> {espessuraSerra} mm</p>
                <p className="text-xs text-neutral-700"><strong>Data:</strong> {new Date().toLocaleDateString('pt-BR')}</p>
              </div>
            </div>
            <div className="relative flex-1 overflow-visible" style={{ height: '245mm' }}>
              <div className="absolute overflow-visible" style={{ left: `${layout.desenhoX}mm`, top: `${layout.desenhoY}mm`, transform: `rotate(${layout.desenhoRotacao}deg)`, transformOrigin: 'top left' }}>
                <div className="relative inline-block">
                  {renderDesenhoSVG(layout.desenhoLargura, layout.desenhoAltura)}
                  {renderCaixasSobreChapa(
                    layout.desenhoLargura * (layout.desenhoEscalaX / 100),
                    layout.desenhoAltura * (layout.desenhoEscalaY / 100),
                    caixasTexto,
                    0,
                    0
                  )}
                </div>
              </div>
              <div className="absolute overflow-hidden bg-[#fafbfc] border-2 border-black" style={{ left: `${layout.infoX}mm`, top: `${layout.infoY}mm`, width: `${layout.infoLargura}mm`, height: `${layout.infoAltura}mm`, transform: `rotate(${layout.infoRotacao}deg)`, transformOrigin: 'top left', boxSizing: 'border-box' }}>
                <div className="p-2" style={{ fontSize: `${(12 * layout.fontSizeInfo) / 100}pt` }}>
                  <h4 className="font-bold mb-1 border-b border-[#009b3a]/22 pb-0.5">Lista de Peças</h4>
                  <table className="w-full border-collapse" style={{ fontSize: `${(10 * layout.fontSizeInfo) / 100}pt` }}>
                    <thead><tr><th className="border border-[#009b3a]/22 px-1 py-0.5 bg-[#dde0e6]">Cód.</th><th className="border border-[#009b3a]/22 px-1 py-0.5 bg-[#dde0e6]">Medida</th><th className="border border-[#009b3a]/22 px-1 py-0.5 bg-[#dde0e6]">Qtd</th></tr></thead>
                    <tbody>
                      {todasPecas.filter((peca) => peca.codigo !== 5).map((peca, i) => (
                        <tr key={i}>
                          <td className="border border-[#009b3a]/22 px-1 py-0.5 text-center" style={{ backgroundColor: peca.codigo === 3 ? '#bbf7d0' : '#bfdbfe' }}>{peca.codigo}</td>
                          <td className="border border-[#009b3a]/22 px-1 py-0.5 text-center">{peca.medida}mm</td>
                          <td className="border border-[#009b3a]/22 px-1 py-0.5 text-center">{peca.quantidade}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <p className="mt-1 text-neutral-700"><strong>Total:</strong> {pecasVisuais.length} peças</p>
                </div>
              </div>
              {layout.observacoes && (
                <div className="absolute bg-amber-50 rounded p-2" style={{ left: `${layout.obsX}mm`, top: `${layout.obsY}mm`, width: `${layout.obsLargura}mm`, transform: `rotate(${layout.obsRotacao}deg)`, transformOrigin: 'top left' }}>
                  <h5 className="font-bold text-black mb-1" style={{ fontSize: `${(12 * layout.obsFontSize) / 100}pt` }}>Observações:</h5>
                  <p className="text-neutral-700 whitespace-pre-wrap" style={{ fontSize: `${(10 * layout.obsFontSize) / 100}pt` }}>{layout.observacoes}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  if (modoCompacto) {
    return <div className="inline-block p-1">{svgElement}</div>;
  }

  // Renderização normal na tela
  return (
    <div className="bg-[#fafbfc] rounded-xl p-4 border border-[#009b3a]/15">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-black text-lg">Plano de Corte Visual</h3>
        <div className="flex items-center gap-2">
          {editarCaixasTexto && (
            <button
              type="button"
              className="flex items-center gap-1.5 rounded-full border border-[#009b3a]/30 bg-[#e8f5ec] px-3 py-1 text-sm font-semibold text-[#009b3a] hover:brightness-[1.03]"
              onClick={() => {
                const nova = criarCaixaTextoPlano();
                onCaixasTextoChange!([...(caixasTexto || []), nova]);
                setCaixaSelecionadaId(nova.id);
              }}
              title="Adicionar caixa de texto sobre o desenho"
            >
              <Type className="h-4 w-4" />
              Caixa de texto
            </button>
          )}
          <button
            type="button"
            className={`px-3 py-1 rounded-full text-sm font-semibold border ${
              lupaAtiva ? 'ade-btn-gold relative z-10 ring-2 ring-[#009b3a]/50' : 'border-[#009b3a]/15 bg-[#e4e6ec] text-neutral-700'
            }`}
            onClick={() => {
              setLupaAtiva((v) => !v);
              setLupaPos(null);
            }}
            title="Ativar/desativar lupa"
          >
            Lupa
          </button>
          {lupaAtiva && (
            <div className="flex items-center gap-2 bg-[#e4e6ec] border border-[#009b3a]/15 rounded-full px-3 py-1">
              <span className="text-xs text-neutral-700">Zoom</span>
              <input
                type="range"
                min={2}
                max={7}
                step={1}
                value={lupaZoom}
                onChange={(e) => setLupaZoom(Number(e.target.value))}
              />
              <span className="text-xs text-black font-semibold">{lupaZoom}×</span>
            </div>
          )}
          <span className="text-sm text-neutral-600 bg-[#e4e6ec] px-3 py-1 rounded-full">📐 {larguraTotal} × {comprimentoTotal} mm</span>
        </div>
      </div>
      {editarCaixasTexto && (caixasTexto?.length ?? 0) > 0 && (
        <p className="mb-2 text-xs text-neutral-600">
          Arraste a barra para mover · laterais esq/dir = altura · topo/baixo = largura (estilo Excel) · ícone{' '}
          <Type className="inline h-3 w-3 align-text-bottom" /> para aumentar a fonte · a caixa pode sair da chapa
        </p>
      )}
      <div className="flex justify-center overflow-visible p-4">
        <div
          className="relative inline-block overflow-visible"
          style={{ transform: `rotate(${rotacao}deg)`, transformOrigin: 'center center' }}
          onMouseLeave={() => setLupaPos(null)}
          onMouseMove={(e) => {
            if (!lupaAtiva) return;
            const rect = (e.currentTarget as HTMLDivElement).getBoundingClientRect();
            const x = e.clientX - rect.left;
            const y = e.clientY - rect.top;
            setLupaPos({ x, y });
          }}
        >
          {svgElement}
          {editarCaixasTexto && onCaixasTextoChange && (
            <CaixasTextoPlanoOverlay
              caixas={caixasTexto}
              onChange={onCaixasTextoChange}
              chapaWidth={svgWidth}
              chapaHeight={svgHeight}
              selectedId={caixaSelecionadaId}
              onSelectId={setCaixaSelecionadaId}
              rotacaoDesenho={rotacao}
            />
          )}
          {!editarCaixasTexto && renderCaixasSobreChapa(svgWidth, svgHeight, caixasTexto)}

          {lupaAtiva && lupaPos && (
            <div
              className="pointer-events-none absolute z-10 rounded-full border-2 border-[#009b3a] bg-[#fafbfc]/90 shadow-neu"
              style={{
                width: lupaTamanhoPx,
                height: lupaTamanhoPx,
                left: Math.max(0, lupaPos.x + 16),
                top: Math.max(0, lupaPos.y + 16),
                overflow: 'hidden',
                backdropFilter: 'blur(2px)',
              }}
            >
              <div
                style={{
                  width: '100%',
                  height: '100%',
                  transformOrigin: 'top left',
                  transform: `scale(${lupaZoom}) translate(${-lupaPos.x + lupaTamanhoPx / (2 * lupaZoom)}px, ${-lupaPos.y + lupaTamanhoPx / (2 * lupaZoom)}px)`,
                }}
              >
                {svgElement}
              </div>
            </div>
          )}
        </div>
      </div>
      {mostrarResumo && (
        <div className="grid grid-cols-4 gap-4 mt-4 p-4 bg-[#e4e6ec] rounded-lg">
          <div className="text-center"><p className="text-2xl font-bold text-[#009b3a]">{pecasVisuais.length}</p><p className="text-xs text-neutral-600">Peças totais</p></div>
          <div className="text-center"><p className="text-2xl font-bold text-amber-600">{grupos.length}</p><p className="text-xs text-neutral-600">Grupos</p></div>
          <div className="text-center"><p className="text-2xl font-bold text-green-600">{Math.max(0, larguraTotal - totalConsumoLargura).toLocaleString('pt-BR')}</p><p className="text-xs text-neutral-600">mm sobra largura</p></div>
          <div className="text-center"><p className="text-2xl font-bold text-blue-600">{Math.max(0, comprimentoTotal - maxConsumoComprimento).toLocaleString('pt-BR')}</p><p className="text-xs text-neutral-600">mm sobra comprimento</p></div>
        </div>
      )}
    </div>
  );
}
